import React, { Component } from "react";
import { Modal, Button } from "react-bootstrap";
import CeoCirclePoster from "./CeoCirclePoster";
import { MAX_PHOTO_BYTES, MIN_YEAR, CITATION_MAX, dataUriToFile, messagesOf, isDuplicateMessage, initialState } from "./ceoCircleFormHelpers";
import "./CeoCircle.css";

/**
 * HR add / edit modal for the CEO Circle of Excellence (styled like the NHO modal).
 * Props: show, onHide, awardee (null = add), currentYear,
 *        searchEmployees(q) -> Promise<list>, fetchBhrPhoto(userId) -> Promise<dataUri>,
 *        onSubmit(formData) -> Promise (rejects with the API error).
 *
 * The Modal portals to document.body (react-bootstrap default) so scroll lock and
 * aria-hidden on the page behave normally; className="ceo-circle" keeps the scoped CSS.
 */
class CeoCircleForm extends Component {
  constructor(props) {
    super(props);
    this.state = { ...initialState(props.awardee, props.currentYear), submitting: false };
    this.fileRef = React.createRef();
    this.searchTimer = null;
    this.searchSeq = 0;
    this.inFlight = false; // synchronous guard for a double-click before setState lands
    this.mounted = false;
  }

  componentDidMount() {
    this.mounted = true;
  }

  componentDidUpdate(prevProps) {
    // Re-opened, or opened for a different awardee: start from a clean draft and make sure a
    // search still in flight from the previous draft cannot fill this one.
    if ((this.props.show && !prevProps.show) || prevProps.awardee !== this.props.awardee) {
      clearTimeout(this.searchTimer);
      this.searchSeq++;
      this.setState(initialState(this.props.awardee, this.props.currentYear));
    }
  }

  componentWillUnmount() {
    this.mounted = false;
    clearTimeout(this.searchTimer);
  }

  setField = (field, value) => {
    this.setState((s) => ({ [field]: value, errors: { ...s.errors, [field]: undefined } }));
  };

  /* ---------- employee picker ---------- */
  onNameChange = (e) => {
    const q = e.target.value;
    this.setState((s) => ({ name: q, userId: null, activeIndex: -1, errors: { ...s.errors, employee: undefined } }));
    clearTimeout(this.searchTimer);
    const trimmed = q.trim();
    if (trimmed.length < 2) {
      this.setState({ options: null });
      return;
    }
    const seq = ++this.searchSeq;
    this.searchTimer = setTimeout(() => {
      Promise.resolve(this.props.searchEmployees(trimmed))
        .catch(() => [])
        .then((list) => {
          if (this.mounted && seq === this.searchSeq) {
            this.setState({ options: Array.isArray(list) ? list : [], activeIndex: -1 });
          }
        });
    }, 250);
  };

  closePicker = () => {
    clearTimeout(this.searchTimer);
    this.searchSeq++;
    this.setState({ options: null, activeIndex: -1 });
  };

  onNameKeyDown = (e) => {
    const opts = this.state.options;
    const open = Array.isArray(opts) && opts.length > 0;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      if (!open) return;
      e.preventDefault();
      const n = opts.length;
      const dir = e.key === "ArrowDown" ? 1 : -1;
      this.setState((s) => ({ activeIndex: (s.activeIndex + dir + n) % n }));
    } else if (e.key === "Enter") {
      if (!open) return;
      e.preventDefault();
      const i = this.state.activeIndex >= 0 ? this.state.activeIndex : 0;
      this.pickEmployee(opts[i]);
    } else if (e.key === "Escape") {
      if (this.state.options === null) return;
      // Only the picker closes; the modal's own Escape handling must not see this one.
      e.preventDefault();
      e.stopPropagation();
      if (e.nativeEvent && e.nativeEvent.stopImmediatePropagation) e.nativeEvent.stopImmediatePropagation();
      this.closePicker();
    }
  };

  pickEmployee = (emp) => {
    if (!emp) return;
    clearTimeout(this.searchTimer);
    this.searchSeq++;
    this.setState((s) => ({
      userId: emp.id,
      name: emp.name,
      jobTitle: emp.job_title || "",
      options: null,
      activeIndex: -1,
      errors: { ...s.errors, employee: undefined, jobTitle: undefined },
    }));
  };

  /* ---------- photo ---------- */
  onFileChange = (e) => {
    const f = e.target.files && e.target.files[0];
    try {
      e.target.value = "";
    } catch (err) {
      /* jsdom / old browsers */
    }
    if (!f) return;
    if (!/^image\/(png|jpeg|webp)$/.test(f.type)) {
      this.setState({ photoMsg: "Please choose a JPG, PNG or WebP image." });
      return;
    }
    if (f.size > MAX_PHOTO_BYTES) {
      this.setState({ photoMsg: "That image is over 8 MB. Please choose a smaller one." });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      if (!this.mounted) return;
      this.setPhoto(f, reader.result, "Uploaded “" + f.name + "”. Use the sliders to frame the face.");
    };
    reader.readAsDataURL(f);
  };

  setPhoto = (file, preview, msg) => {
    this.setState({ photoFile: file, photoPreview: preview, removePhoto: false, posY: 30, zoom: 108, photoMsg: msg });
  };

  onFetchBhr = () => {
    if (!this.state.userId) {
      this.setState({ photoMsg: "Pick the employee first, then fetch their BHR photo." });
      return;
    }
    return Promise.resolve(this.props.fetchBhrPhoto(this.state.userId))
      .then((uri) => {
        const file = dataUriToFile(uri);
        if (!file) throw new Error("bad photo");
        if (this.mounted) this.setPhoto(file, uri, "Photo fetched from BHR. Keep it, or upload a different one.");
      })
      .catch(() => {
        if (this.mounted) this.setState({ photoMsg: "BHR has no photo for this employee. Please upload one." });
      });
  };

  onRemovePhoto = () => {
    const hadSaved = !!(this.props.awardee && this.props.awardee.photo_url);
    this.setState({ photoFile: null, photoPreview: null, removePhoto: hadSaved, photoMsg: "Photo removed. The poster will show initials." });
  };

  /* ---------- submit ---------- */
  validate = () => {
    const { userId, year, jobTitle, citation } = this.state;
    const errors = {};
    const y = parseInt(year, 10);
    if (!userId) errors.employee = "Pick an employee from the list.";
    if (!(y >= MIN_YEAR && y <= Number(this.props.currentYear))) errors.year = "Enter a valid year.";
    if (!jobTitle.trim()) errors.jobTitle = "Job title is required.";
    if (!citation.trim()) errors.citation = "Citation is required.";
    return errors;
  };

  onSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (this.inFlight) return; // a save is already on its way (double-click)
    const errors = this.validate();
    if (Object.keys(errors).length) {
      this.setState({ errors, serverErrors: [] });
      return;
    }
    const s = this.state;
    const fd = new FormData();
    fd.append("user_id", String(s.userId));
    fd.append("year", String(parseInt(s.year, 10)));
    fd.append("job_title", s.jobTitle.trim());
    fd.append("citation", s.citation.trim());
    if (s.photoFile) {
      if (s.photoFile.name) fd.append("photo", s.photoFile);
      else fd.append("photo", s.photoFile, "bhr-photo.jpg");
    } else if (s.removePhoto) {
      fd.append("remove_photo", "1");
    }
    fd.append("photo_pos_y", String(s.posY));
    fd.append("photo_zoom", String(s.zoom));

    this.inFlight = true;
    this.setState({ submitting: true, serverErrors: [] });
    const settle = () => {
      this.inFlight = false;
      if (this.mounted) this.setState({ submitting: false });
    };
    return Promise.resolve()
      .then(() => this.props.onSubmit(fd))
      .then(() => {
        settle();
        if (this.mounted) this.props.onHide();
      })
      .catch((err) => {
        settle();
        // A 422 is shown in full at the top of the form; only the duplicate user+year rule
        // (the one rule the client cannot pre-check) also lands on the Year field.
        if (this.mounted && err && err.status === 422) {
          const messages = messagesOf(err);
          const duplicate = messages.some(isDuplicateMessage);
          this.setState((st) => ({
            serverErrors: messages,
            errors: { ...st.errors, year: duplicate ? "" : undefined },
          }));
        }
      });
  };

  render() {
    const { show, onHide, awardee, currentYear } = this.props;
    const s = this.state;
    const yearInvalid = s.errors.year !== undefined;
    const hasPhoto = !!s.photoPreview;
    const hasServerErrors = s.serverErrors.length > 0;

    const preview = {
      name: s.userId ? s.name : s.name.trim(),
      year: s.year,
      job_title: s.jobTitle.trim(),
      citation: s.citation.trim(),
      photo_url: s.photoPreview,
      photo_pos_y: s.posY,
      photo_zoom: s.zoom,
    };

    const describedBy = (...ids) => ids.filter(Boolean).join(" ") || undefined;
    const activeOption =
      s.options && s.activeIndex >= 0 && s.options[s.activeIndex] ? "ceo-emp-opt-" + s.options[s.activeIndex].id : undefined;

    return (
      <Modal
        show={show}
        onHide={onHide}
        size="xl"
        backdrop="static"
        className="ceo-circle"
        aria-labelledby="ceo-circle-modal-title"
      >
        <form onSubmit={this.onSubmit} noValidate>
          <Modal.Header className="ceo-circle-modal-header" closeButton>
            <Modal.Title id="ceo-circle-modal-title">{awardee ? "Edit Awardee" : "Add Awardee"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {hasServerErrors && (
              <div className="alert alert-danger" role="alert" id="ceo-circle-form-errors">
                <strong>The awardee could not be saved.</strong>
                <ul className="mb-0 pl-3">
                  {s.serverErrors.map((m, i) => (
                    <li key={i}>{m}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="form-grid">
              <div>
                <p className="text-muted" style={{ fontSize: 13 }}>
                  Pick the employee and year, write the citation and add a photo. The poster is generated automatically.
                </p>

                <div className="form-group">
                  <label className="survey-label nho-required" htmlFor="ceo-emp">Employee</label>
                  <div className="combo">
                    <input
                      type="text"
                      className={"form-control" + (s.errors.employee ? " is-invalid" : "")}
                      id="ceo-emp"
                      autoComplete="off"
                      placeholder="Search by name…"
                      role="combobox"
                      aria-expanded={s.options ? "true" : "false"}
                      aria-controls="ceo-emp-list"
                      aria-autocomplete="list"
                      aria-activedescendant={activeOption}
                      aria-invalid={s.errors.employee ? "true" : "false"}
                      aria-describedby={describedBy(s.errors.employee && "ceo-emp-error")}
                      value={s.name}
                      onChange={this.onNameChange}
                      onKeyDown={this.onNameKeyDown}
                      onBlur={() => s.options !== null && this.closePicker()}
                    />
                    {s.options && (
                      <div className="dropdown-menu" id="ceo-emp-list" role="listbox">
                        {s.options.length ? (
                          s.options.map((emp, i) => (
                            <button
                              type="button"
                              className={"dropdown-item" + (i === s.activeIndex ? " active" : "")}
                              role="option"
                              id={"ceo-emp-opt-" + emp.id}
                              aria-selected={i === s.activeIndex ? "true" : "false"}
                              key={emp.id}
                              tabIndex={-1}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => this.pickEmployee(emp)}
                            >
                              {emp.name}
                              <small>{emp.job_title || ""}</small>
                            </button>
                          ))
                        ) : (
                          <span className="dropdown-item-text text-muted">No employee matches</span>
                        )}
                      </div>
                    )}
                    {s.errors.employee && <div className="invalid-feedback" id="ceo-emp-error">{s.errors.employee}</div>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group col-sm-4">
                    <label className="survey-label nho-required" htmlFor="ceo-year">Year</label>
                    <input
                      type="number"
                      className={"form-control" + (yearInvalid ? " is-invalid" : "")}
                      id="ceo-year"
                      min={MIN_YEAR}
                      max={currentYear}
                      step="1"
                      aria-invalid={yearInvalid ? "true" : "false"}
                      aria-describedby={describedBy(
                        s.errors.year && "ceo-year-error",
                        yearInvalid && hasServerErrors && "ceo-circle-form-errors"
                      )}
                      value={s.year}
                      onChange={(e) => this.setField("year", e.target.value)}
                    />
                    {s.errors.year && <div className="invalid-feedback" id="ceo-year-error">{s.errors.year}</div>}
                  </div>
                  <div className="form-group col-sm-8">
                    <label className="survey-label nho-required" htmlFor="ceo-title">Job Title</label>
                    <span className="auto-pill">from employee record</span>
                    <input
                      type="text"
                      className={"form-control" + (s.errors.jobTitle ? " is-invalid" : "")}
                      id="ceo-title"
                      maxLength={100}
                      placeholder="Fills in when you pick the employee"
                      aria-invalid={s.errors.jobTitle ? "true" : "false"}
                      aria-describedby={describedBy("ceo-title-help", s.errors.jobTitle && "ceo-title-error")}
                      value={s.jobTitle}
                      onChange={(e) => this.setField("jobTitle", e.target.value)}
                    />
                    <small className="form-text text-muted" id="ceo-title-help">
                      Saved as it is today, so the poster stays right if they change roles.
                    </small>
                    {s.errors.jobTitle && <div className="invalid-feedback" id="ceo-title-error">{s.errors.jobTitle}</div>}
                  </div>
                </div>

                <div className="form-group">
                  <label className="survey-label nho-required" htmlFor="ceo-cite">Citation</label>
                  <textarea
                    className={"form-control" + (s.errors.citation ? " is-invalid" : "")}
                    id="ceo-cite"
                    rows={2}
                    maxLength={CITATION_MAX}
                    placeholder="Awarded for…"
                    aria-invalid={s.errors.citation ? "true" : "false"}
                    aria-describedby={describedBy("ceo-cite-count", s.errors.citation && "ceo-cite-error")}
                    value={s.citation}
                    onChange={(e) => this.setField("citation", e.target.value.slice(0, CITATION_MAX))}
                  />
                  <small className="form-text text-muted" id="ceo-cite-count">
                    <span>{s.citation.length}</span>/{CITATION_MAX} characters
                  </small>
                  {s.errors.citation && <div className="invalid-feedback" id="ceo-cite-error">{s.errors.citation}</div>}
                </div>

                <div className="form-group">
                  <span className="survey-label d-block">Photo</span>
                  <Button type="button" size="sm" className="btn-primary-2 mr-1" onClick={() => this.fileRef.current && this.fileRef.current.click()}>
                    <i className="fa fa-upload" aria-hidden="true" /> Upload Photo
                  </Button>
                  <Button type="button" size="sm" variant="outline-secondary" className="mr-1" onClick={this.onFetchBhr}>
                    <i className="fa fa-cloud-download" aria-hidden="true" /> Fetch from BHR
                  </Button>
                  {hasPhoto && (
                    <Button type="button" size="sm" variant="link" className="text-danger" onClick={this.onRemovePhoto}>
                      Remove
                    </Button>
                  )}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    style={{ display: "none" }}
                    ref={this.fileRef}
                    onChange={this.onFileChange}
                  />
                  <div className="photo-msg" aria-live="polite">{s.photoMsg}</div>
                  {hasPhoto && (
                    <div className="sliders">
                      <label htmlFor="ceo-pos" className="m-0">Position</label>
                      <input type="range" className="custom-range" id="ceo-pos" min="0" max="100" value={s.posY} onChange={(e) => this.setState({ posY: Number(e.target.value) })} />
                      <label htmlFor="ceo-zoom" className="m-0">Zoom</label>
                      <input type="range" className="custom-range" id="ceo-zoom" min="100" max="180" value={s.zoom} onChange={(e) => this.setState({ zoom: Number(e.target.value) })} />
                    </div>
                  )}
                </div>
              </div>

              <div className="preview-col">
                <div className="lbl">Live preview</div>
                <div className="poster-wrap">
                  <CeoCirclePoster awardee={preview} label="Live preview of the poster" />
                </div>
                <small className="text-muted d-block mt-2">Header, badge, gold ring and footer tagline are fixed in the template.</small>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button type="button" variant="secondary" onClick={onHide} disabled={s.submitting}>Cancel</Button>
            <Button type="submit" className="btn-primary-2" disabled={s.submitting}>
              <i className="fa fa-location-arrow" aria-hidden="true" /> Save &amp; Publish
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
    );
  }
}

export default CeoCircleForm;
